import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import ContextualMobileHeader from './ContextualMobileHeader';
import Sidebar from './Sidebar';
import BottomTabBar, { isTerminalRoute } from './BottomTabBar';
import { ToastContainer } from './Toast';
import {
  IntakeModalProvider,
  useIntakeModalContext,
} from '../contexts/IntakeModalContext';
import IntakeModal from './Modals/IntakeModal';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useProfile } from '../hooks/useProfile';
import { useAuth0 } from '@auth0/auth0-react';
import { usePullToRefresh } from '../hooks/useRefresh';
import { LoaderCircle } from 'lucide-react';
import { RaceTrainer } from '../assets';
import { dayjs } from '../lib/utils';
import { useSaveSharedPlannedWorkout } from '../hooks/mutations/useSaveSharedPlannedWorkout';

const SharedWorkoutLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    loginWithRedirect,
  } = useAuth0();
  const saveSharedWorkout = useSaveSharedPlannedWorkout();
  const plannedWorkoutMatch = location.pathname.match(
    /^\/shared\/planned-workouts\/([^/]+)$/,
  );

  const save = async () => {
    if (!plannedWorkoutMatch) return;

    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: location.pathname },
        authorizationParams: { screen_hint: 'signup' },
      });
      return;
    }

    saveSharedWorkout.mutate(
      {
        token: decodeURIComponent(plannedWorkoutMatch[1]),
        plannedDate: dayjs().format('YYYY-MM-DD'),
      },
      {
        onSuccess: (workout) =>
          navigate({
            to: '/plan/workout/$id',
            params: { id: String(workout.id) },
          }),
        onError: () => window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Unable to save this workout. Try again.', type: 'error' } })),
      },
    );
  };

  return (
    <div className="min-h-dvh overflow-y-auto bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-brand-midnight/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-8">
          <a href="https://racetrainer.ai" aria-label="RaceTrainer home">
            {/* @ts-ignore */}
            <RaceTrainer className="invert" width={132} height={48} />
          </a>
          <nav aria-label="Account">
            {plannedWorkoutMatch ? (
              <button
                type="button"
                onClick={save}
                disabled={isAuthLoading || saveSharedWorkout.isPending}
                className="rounded-lg bg-brand-volt px-5 py-2.5 text-sm font-bold text-brand-midnight transition-colors hover:bg-brand-volt/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saveSharedWorkout.isPending ? 'Saving…' : 'Save'}
              </button>
            ) : (
              <Link
                to="/signup"
                className="rounded-lg bg-brand-volt px-5 py-2.5 text-sm font-bold text-brand-midnight transition-colors hover:bg-brand-volt/90"
              >
                Join
              </Link>
            )}
          </nav>
        </div>
      </header>
      <Outlet />
    </div>
  );
};

const LayoutContent = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { isOpen: intakeModalOpen, closeModal: closeIntakeModal } =
    useIntakeModalContext();
  const { user } = useAuth0();
  const { data: profile } = useProfile();
  const {
    isPulling,
    pullDistance,
    isRefreshing,
    refreshThreshold,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = usePullToRefresh();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    // Set initial state after component mount
    setTimeout(handleResize, 0);

    // Add event listener
    window.addEventListener('resize', handleResize);

    if (location.pathname === '/success') {
      setSidebarOpen(false);
    }

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleSidebarChange = (e: CustomEvent) => {
      setSidebarOpen(e.detail.isOpen);
    };
    window.addEventListener(
      'sidebarStateChange',
      handleSidebarChange as EventListener,
    );

    return () =>
      window.removeEventListener(
        'sidebarStateChange',
        handleSidebarChange as EventListener,
      );
  }, []);

  useLayoutEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      mainRef.current.scrollLeft = 0;
    }
  }, [location.pathname]);

  const contextualHeader = (() => {
    if (location.pathname === '/plan/create') {
      return { title: 'previous page' };
    }

    if (
      location.pathname === '/plan/workout/new' ||
      location.pathname === '/plan/workout/new/manual' ||
      location.pathname.match(/^\/plan\/workout\/[^/]+\/(edit|duplicate)$/)
    ) {
      return { title: 'previous page' };
    }

    if (location.pathname.match(/^\/races\/[^/]+$/)) {
      return { title: 'previous page' };
    }

    if (location.pathname.match(/^\/plan\/workout\/[^/]+$/)) {
      return { title: 'previous page' };
    }

    if (location.pathname.match(/^\/workouts\/[^/]+$/)) {
      return { title: 'previous page' };
    }

    return null;
  })();
  const terminalRoute = isTerminalRoute(location.pathname);
  const isSharedWorkout = /^\/shared\/(?:planned-)?workouts\/[^/]+$/.test(
    location.pathname,
  );

  // Add touch event listeners for pull-to-refresh on mobile
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, handleTouchStart, handleTouchMove, handleTouchEnd]);

  const indicatorVisible = isPulling || isRefreshing;
  const pullProgress = Math.min(pullDistance / refreshThreshold, 1);
  const contentOffset = isMobile
    ? isRefreshing
      ? refreshThreshold
      : Math.min(pullDistance, refreshThreshold)
    : 0;

  return isSharedWorkout ? (
    <SharedWorkoutLayout />
  ) : (
    <div className="relative flex h-dvh flex-col overflow-hidden bg-background text-foreground">
      {/* Pull-to-refresh indicator */}
      {indicatorVisible ? (
        <div
          className="pointer-events-none fixed left-0 right-0 top-0 z-40 flex justify-center transition-all duration-200 ease-out md:hidden"
          style={{
            opacity: isRefreshing ? 1 : pullProgress,
            transform: `translateY(${Math.max(contentOffset - 44, 8)}px)`,
          }}
          aria-hidden={!indicatorVisible}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-midnight shadow-lg ring-1 ring-black/5">
            <LoaderCircle
              className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
              style={{
                transform: isRefreshing
                  ? undefined
                  : `rotate(${Math.round(pullProgress * 180)}deg)`,
              }}
            />
          </div>
        </div>
      ) : null}

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden transition-transform duration-200 ease-out"
        style={{ transform: `translateY(${contentOffset}px)` }}
      >
        {contextualHeader ? (
          <ContextualMobileHeader {...contextualHeader} />
        ) : null}
        {/* Mobile and desktop sidebar - now handled by Sidebar component itself */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {sidebarOpen ? <Sidebar /> : null}
          <main
            ref={mainRef}
            className={`min-h-0 flex-1 overflow-y-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${terminalRoute ? 'pb-0' : 'pb-[calc(6rem+env(safe-area-inset-bottom))]'} md:px-14 md:pt-14 md:pb-14 xl:px-20 ${
              contextualHeader ? 'pt-14 md:pt-14' : 'pt-2 md:pt-14'
            } ${isMobile ? 'w-full' : ''}`}
          >
            <div className="app-page-surface w-full max-w-[1508px] mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      {/* Intake Modal */}
      <IntakeModal
        isOpen={intakeModalOpen}
        onClose={closeIntakeModal}
        user={{
          name: user?.name || profile?.name,
          email: user?.email || profile?.email,
        }}
      />
      {/* Toast notifications */}
      <ToastContainer />
      {/* Mobile bottom tab bar */}
      <BottomTabBar />
    </div>
  );
};

const Layout = () => {
  const location = useLocation();
  const isSharedWorkout = /^\/shared\/(?:planned-)?workouts\/[^/]+$/.test(
    location.pathname,
  );
  const isPublicRoute = [
    '/success',
    '/error',
    '/support',
    '/login',
    '/signup',
    '/callback',
  ].includes(location.pathname);

  return isSharedWorkout ? (
    <SharedWorkoutLayout />
  ) : isPublicRoute ? (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="app-page-surface mx-auto min-h-dvh w-full max-w-5xl px-4 py-6 sm:px-8 sm:py-10">
        <Outlet />
      </div>
    </div>
  ) : (
    <IntakeModalProvider>
      <LayoutContent />
    </IntakeModalProvider>
  );
};

export default Layout;
